import {
  FlexibleBlockBrickButton,
  FlexibleBlockBrickImage,
  FlexibleBlockBrickText,
  FlexibleBlockProps,
  FlexibleBlockTemplate,
  FlexibleBlockBrick,
  FlexibleBlockTemplateInfoCard,
  FlexibleBlockBrickTitle,
  PortalTextType,
  FlexibleBlockBrickMeta,
  TailwindCommonPalette,
  DynamicLinkType,
} from '@nx-frontend/shared/types';
import { getWrapperProps } from '@nx-frontend/shared/utils';
import { FlexibleBlockView, brickCommon } from './common';

export const getInfoCardBlockTemplate = (): FlexibleBlockProps<FlexibleBlockTemplateInfoCard>[] => {
  const brickImage: FlexibleBlockBrickImage = {
    ...brickCommon,
    brickType: 'image',
    brickName: 'Image',
    switchable: true,
    src: '/assets/img/blocks/card/card-img.jpg',
    className: [],
  };

  const brickTitle: FlexibleBlockBrickTitle = {
    ...brickCommon,
    brickType: 'title',
    brickName: 'Heading',
    switchable: true,
    placeholder: 'Here, you can add title',
    text: 'Helpful Resources',
    className: ['font-bold'],
    theme: {
      text: {
        main: PortalTextType.SURFACE,
      },
    },
  };

  const brickDescription: FlexibleBlockBrickText = {
    ...brickCommon,
    brickType: 'text',
    brickName: 'Description',
    switchable: true,
    placeholder: 'Here, you can add some information about widget',
    text: 'Here are tools and sites we think you`ll find valuable for increasing efficiency.',
    theme: {
      text: {
        main: PortalTextType.SURFACE_3,
      },
    },
  };

  const brickButton: FlexibleBlockBrickButton = {
    ...brickCommon,
    brickType: 'button',
    brickName: 'Button',
    switchable: true,
    text: 'Explore',
    noLink: true,
  };
  const brickMeta: FlexibleBlockBrickMeta = {
    ...brickCommon,
    brickType: 'meta',
    brickName: 'Destination URL',
    switchable: false,
    dynamicLink: { type: DynamicLinkType.EXTERNAL, url: 'https://en.wikipedia.org/wiki/Project' },
  };

  const block: FlexibleBlockProps<FlexibleBlockTemplateInfoCard> = {
    title: brickTitle.text,
    template: {
      type: 'info-card',
      view: 'top-image',
    },
    bricks: [
      { ...brickImage, index: 0 },
      { ...brickTitle, index: 1 },
      { ...brickDescription, index: 2 },
      { ...brickButton, index: 3 },
      { ...brickMeta, index: 4 },
    ],
    className: ['text-sm'],
    wrapper: getWrapperProps('bare'),
  };

  return [
    convertInfoCardBlockView(block, {
      type: 'info-card',
      view: 'top-image',
    }),
    convertInfoCardBlockView(block, {
      type: 'info-card',
      view: 'left-image',
    }),
    convertInfoCardBlockView(block, {
      type: 'info-card',
      view: 'right-image',
    }),
    convertInfoCardBlockView(block, {
      type: 'info-card',
      view: 'fill-image',
    }),
  ];
};

export const convertInfoCardBlockView = <T extends FlexibleBlockTemplate = FlexibleBlockTemplateInfoCard>(
  block: FlexibleBlockProps<T>,
  template: T
): FlexibleBlockProps<T> => {
  const newBlock = { ...block, template };
  newBlock.className = template.view === 'right-image' ? ['flex-col'] : [];
  newBlock.bricks = newBlock.bricks.reduce<FlexibleBlockBrick[]>((acc, brick) => {
    if (brick.brickType === 'title') {
      acc.push({
        ...brick,
        theme: {
          ...(brick.theme ?? null),
          text: {
            ...(brick.theme?.text ?? null),
            main: template.view === 'fill-image' ? [TailwindCommonPalette.WHITE] : PortalTextType.SURFACE,
          },
        },
      });
    } else if (brick.brickType === 'text') {
      acc.push({
        ...brick,
        theme: {
          ...(brick.theme ?? null),
          text: {
            ...(brick.theme?.text ?? null),
            main: template.view === 'fill-image' ? [TailwindCommonPalette.WHITE] : PortalTextType.SURFACE,
          },
        },
      });
    } else {
      acc.push(brick);
    }
    return acc;
  }, []);

  return newBlock;
};

export const getInfoCardBlockViews = (): FlexibleBlockView<FlexibleBlockTemplateInfoCard>[] => {
  return [
    {
      key: 'top-image',
      template: { type: 'info-card', view: 'top-image' },
      title: 'Card with image',
      description:
        'Recommended card width: at least 2 columns. Single-column layout may look different and less appealing.',
      image: '/assets/img/blocks/card/view-card-with-image.png',
      preview: '/assets/img/blocks/card/preview-card-with-image.png',
    },
    {
      key: 'left-image',
      template: { type: 'info-card', view: 'left-image' },
      title: 'Card with left image',
      description:
        'Recommended card width: at least 2 columns. Single-column layout may look different and less appealing.',
      image: '/assets/img/blocks/card/view-card-with-left-image.png',
      preview: '/assets/img/blocks/card/preview-card-with-left-image.png',
    },
    {
      key: 'right-image',
      template: { type: 'info-card', view: 'right-image' },
      title: 'Card with right image',
      description:
        'Recommended card width: at least 2 columns. Single-column layout may look different and less appealing.',
      image: '/assets/img/blocks/card/view-card-with-right-image.png',
      preview: '/assets/img/blocks/card/preview-card-with-right-image.png',
    },
    {
      key: 'fill-image',
      template: { type: 'info-card', view: 'fill-image' },
      title: 'Card with background image',
      description:
        'Recommended card width: at least 2 columns. Single-column layout may look different and less appealing.',
      image: '/assets/img/blocks/card/view-card-with-bg-image.png',
      preview: '/assets/img/blocks/card/preview-card-with-bg-image.png',
    },
  ];
};
